import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ChevronLeft,
    Monitor,
    Edit,
    Archive,
    RefreshCw,
    Trash2,
    Settings,
    Download,
    ExternalLink,
    AlertTriangle,
    CheckCircle2,
    XCircle,
    CreditCard,
    Phone,
    Mail,
    Copy,
    Clock,
    Users,
    UserPlus,
    UserMinus,
    Search,
    AlertCircle,
    Building2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { MultiSelect } from '../../components/ui/multi-select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import api from '../../utils/api';
import { toast } from 'sonner';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from '../../components/AccessRestricted';

const BusinessSystemDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);

    const [system, setSystem] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState<any[]>([]);
    const [userSearch, setUserSearch] = useState('');
    const [showAddUser, setShowAddUser] = useState(false);
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [showArchiveAlert, setShowArchiveAlert] = useState(false);
    const [showRestoreAlert, setShowRestoreAlert] = useState(false);
    const [allUsers, setAllUsers] = useState<any[]>([]);
    const [addUserForm, setAddUserForm] = useState({ userIds: [] as string[], role: 'User' });
    const [userToRemove, setUserToRemove] = useState<number | null>(null);
    const [blockedAction, setBlockedAction] = useState<{ type: 'archive' | 'delete', name: string } | null>(null);

    if (!ability.can(ACTIONS.READ, RESOURCES.BUSINESS_SYSTEM)) {
        return <AccessRestricted />;
    }

    useEffect(() => {
        if (id) fetchSystem();
    }, [id]);

    const fetchSystem = async () => {
        try {
            setLoading(true);
            const data = await api.get(`/api/business-systems/${id}`);
            setSystem(data);
        } catch (err) {
            console.error('Error fetching business system:', err);
            toast.error('Failed to load system details');
        } finally {
            setLoading(false);
        }
    };

    const fetchAllUsers = async () => {
        try {
            const res = await api.get('/api/users?all_users=true');
            setAllUsers(res.users || []);
        } catch (err) {
            console.error('Error fetching users:', err);
        }
    };

    const handleAddUser = async () => {
        if (!addUserForm.userIds.length) {
            toast.error('Please select at least one user');
            return;
        }
        try {
            const results = await Promise.allSettled(
                addUserForm.userIds.map(userId =>
                    api.post(`/api/business-systems/${id}/users`, { userId, role: addUserForm.role })
                )
            );
            const succeeded = results.filter(r => r.status === 'fulfilled').length;
            const failed = results.filter(r => r.status === 'rejected').length;
            if (succeeded > 0 && failed === 0) {
                toast.success(`${succeeded} user${succeeded > 1 ? 's' : ''} added successfully`);
            } else if (succeeded > 0 && failed > 0) {
                toast.success(`${succeeded} user${succeeded > 1 ? 's' : ''} added, ${failed} failed`);
            } else {
                toast.error('Failed to add users');
            }
            setShowAddUser(false);
            setAddUserForm({ userIds: [], role: 'User' });
            fetchSystem();
        } catch (err: any) {
            toast.error(err.message || 'Failed to add users');
        }
    };

    const handleRemoveUser = async () => {
        if (!userToRemove) return;
        try {
            await api.delete(`/api/business-systems/${id}/users/${userToRemove}`);
            toast.success('User removed successfully');
            setUserToRemove(null);
            fetchSystem();
        } catch (err: any) {
            toast.error(err.message || 'Failed to remove user');
            setUserToRemove(null);
        }
    };

    const handleArchive = async () => {
        try {
            await api.put(`/api/business-systems/${id}`, { status: 'Archived' });
            toast.success('System archived successfully');
            setShowArchiveAlert(false);
            fetchSystem();
        } catch (err: any) {
            toast.error(err.message || 'Failed to archive system');
        }
    };

    const handleRestore = async () => {
        try {
            await api.put(`/api/business-systems/${id}`, { status: 'Active' });
            toast.success('System restored successfully');
            setShowRestoreAlert(false);
            fetchSystem();
        } catch (err: any) {
            toast.error(err.message || 'Failed to restore system');
        }
    };

    const handleDelete = async () => {
        try {
            await api.delete(`/api/business-systems/${id}`);
            toast.success('System deleted successfully');
            navigate('/business-systems');
        } catch (err: any) {
            toast.error(err.message || 'Failed to delete system');
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    // Detect issues
    const getIssues = () => {
        if (!system) return [];
        const issues: string[] = [];
        if (!system.contractNumber) issues.push('No contract details');
        if (system.twoFactorAuth === 'Disabled') issues.push('2FA is disabled');
        if (system.totalLicenses > 0 && system.usedLicenses > system.totalLicenses) issues.push('License usage exceeds total');
        if (!system.vendor) issues.push('No vendor information');
        return issues;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!system) {
        return (
            <div className="text-center py-20">
                <h2 className="text-2xl font-bold text-gray-900">System not found</h2>
                <Button onClick={() => navigate('/business-systems')} className="mt-4">Back to Systems</Button>
            </div>
        );
    }

    const issues = getIssues();
    const availableLicenses = Math.max(0, (system.totalLicenses || 0) - (system.usedLicenses || 0));
    const utilization = system.totalLicenses > 0 ? Math.round((system.usedLicenses / system.totalLicenses) * 100) : 0;
    const isPerUserModel = system.costModel === 'Per user per month' || system.costModel === 'Per user per year';
    const costPerLicense = system.totalLicenses > 0 && system.monthlyCost && isPerUserModel
        ? system.costModel === 'Per user per year'
            ? ((parseFloat(system.monthlyCost) * 12) / system.totalLicenses).toFixed(2)
            : (parseFloat(system.monthlyCost) / system.totalLicenses).toFixed(2)
        : '0.00';
    const annualCost = system.monthlyCost ? (parseFloat(system.monthlyCost) * 12).toFixed(2) : '0.00';

    const assignedUsers = system.assignedUsers || [];
    const filteredUsers = assignedUsers.filter((au: any) => {
        if (!userSearch) return true;
        const u = au.user;
        const search = userSearch.toLowerCase();
        return (
            (u?.name?.toLowerCase().includes(search)) ||
            (u?.email?.toLowerCase().includes(search)) ||
            (au.role?.toLowerCase().includes(search)) ||
            (u?.department?.departmentName?.toLowerCase().includes(search))
        );
    });

    return (
        <div className="max-w-[1400px] mx-auto pb-20 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8">
                <div className="flex items-center gap-4">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-xl border border-gray-100 bg-white hover:bg-gray-50 shrink-0 gap-2 text-xs font-bold text-gray-600"
                        onClick={() => navigate('/business-systems')}
                    >
                        <ChevronLeft size={16} />
                        Back to Systems
                    </Button>
                    <div className="p-3 rounded-2xl bg-slate-100 text-slate-700 shrink-0">
                        <Monitor size={20} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{system.name}</h1>
                        <p className="text-gray-500 text-sm font-medium mt-0.5">{system.description || 'No description'}</p>
                    </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    {ability.can(ACTIONS.UPDATE, RESOURCES.BUSINESS_SYSTEM) && (
                        <Button
                            variant="outline"
                            className="rounded-xl h-10 border-gray-100 font-bold text-xs gap-2 bg-white"
                            onClick={() => navigate(`/business-systems/${id}/edit`)}
                        >
                            <Edit size={14} />
                            Edit
                        </Button>
                    )}
                    {ability.can(ACTIONS.UPDATE, RESOURCES.BUSINESS_SYSTEM) && system.status !== 'Archived' && (
                        <Button
                            variant="outline"
                            className="rounded-xl h-10 border-gray-100 font-bold text-xs gap-2 bg-white"
                            onClick={() => {
                                if (system.assignedUsers?.length > 0) {
                                    setBlockedAction({ type: 'archive', name: system.name });
                                } else {
                                    setShowArchiveAlert(true);
                                }
                            }}
                        >
                            <Archive size={14} />
                            Archive
                        </Button>
                    )}
                    {ability.can(ACTIONS.UPDATE, RESOURCES.BUSINESS_SYSTEM) && system.status === 'Archived' && (
                        <Button
                            variant="outline"
                            className="rounded-xl h-10 border-green-200 text-green-700 font-bold text-xs gap-2 bg-green-50 hover:bg-green-100"
                            onClick={() => setShowRestoreAlert(true)}
                        >
                            <RefreshCw size={14} />
                            Restore
                        </Button>
                    )}
                    {ability.can(ACTIONS.DELETE, RESOURCES.BUSINESS_SYSTEM) && (
                        <Button
                            className="rounded-xl h-10 bg-red-600 hover:bg-red-700 text-white font-bold text-xs gap-2"
                            onClick={() => {
                                if (system.assignedUsers?.length > 0) {
                                    setBlockedAction({ type: 'delete', name: system.name });
                                } else {
                                    setShowDeleteAlert(true);
                                }
                            }}
                        >
                            <Trash2 size={14} />
                            Delete
                        </Button>
                    )}
                </div>
            </div>

            {/* Issues Banner */}
            {issues.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-2xl p-5 mb-6">
                    <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle size={18} className="text-orange-600" />
                        <h4 className="font-bold text-orange-800">Issues Detected</h4>
                    </div>
                    <p className="text-orange-700 text-sm mb-2">This system has {issues.length} issue{issues.length > 1 ? 's' : ''} that need attention:</p>
                    <ul className="space-y-1">
                        {issues.map((issue, i) => (
                            <li key={i} className="text-sm text-orange-700 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-orange-600" />
                                {issue}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* System Overview */}
                    <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <div className="flex items-center justify-between mb-6">
                            <h4 className="font-bold text-gray-800">System Overview</h4>
                            <Badge className={`rounded-full text-xs font-bold px-3 py-1 ${system.status === 'Active' ? 'bg-green-50 text-green-700 border-green-200' :
                                system.status === 'Archived' ? 'bg-gray-100 text-gray-600 border-gray-200' :
                                    'bg-red-50 text-red-600 border-red-200'
                                }`}>
                                {system.status}
                            </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-y-5 gap-x-8">
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Category</p>
                                <p className="text-sm font-bold text-gray-800 mt-0.5">{system.category || '—'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Department</p>
                                <p className="text-sm font-bold text-gray-800 mt-0.5">{system.department || '—'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Purchase Mode</p>
                                <p className="text-sm font-bold text-gray-800 mt-0.5">{system.purchaseMode || '—'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 font-medium">2FA Status</p>
                                <div className="mt-0.5">
                                    {system.twoFactorAuth === 'Enabled' ? (
                                        <Badge className="bg-green-50 text-green-700 border-green-200 text-xs font-bold gap-1">
                                            <CheckCircle2 size={12} /> Enabled
                                        </Badge>
                                    ) : system.twoFactorAuth === 'Disabled' ? (
                                        <Badge className="bg-red-50 text-red-600 border-red-200 text-xs font-bold gap-1">
                                            <XCircle size={12} /> Disabled
                                        </Badge>
                                    ) : (
                                        <span className="text-sm text-gray-400 font-medium">N/A</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Cost Information */}
                    <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-2 mb-6">
                            <span className="text-gray-800 font-bold">💲</span>
                            <h4 className="font-bold text-gray-800">Cost Information</h4>
                        </div>
                        <div className="grid grid-cols-2 gap-y-5 gap-x-8">
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Monthly Cost</p>
                                <p className="text-xl font-bold text-gray-900 mt-0.5">${system.monthlyCost ? parseFloat(system.monthlyCost).toFixed(2) : '0.00'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Cost Model</p>
                                <p className="text-sm font-bold text-gray-800 mt-0.5">{system.costModel || '—'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Cost per License</p>
                                <p className="text-xl font-bold text-gray-900 mt-0.5">${isPerUserModel ? costPerLicense : '—'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Annual Cost</p>
                                <p className="text-xl font-bold text-gray-900 mt-0.5">${annualCost}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Payment Method</p>
                                <div className="flex items-center gap-2 mt-1">
                                    <CreditCard size={16} className="text-gray-400" />
                                    <span className="text-sm font-bold text-gray-800">{system.paymentMethod || '—'}</span>
                                    {system.paymentDetails && (
                                        <span className="text-sm text-gray-500">{system.paymentDetails}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>
 
                    {/* Vendor & Contract Information */}
                    <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-2 mb-6">
                            <Building2 size={18} className="text-gray-800" />
                            <h4 className="font-bold text-gray-800">Vendor & Contract Information</h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-5 gap-x-8">
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Vendor</p>
                                <p className="text-sm font-bold text-gray-800 mt-0.5">{system.vendor || '—'}</p>
                            </div>
                            {system.website && (
                                <div>
                                    <p className="text-xs text-gray-400 font-medium">Website</p>
                                    <div className="flex items-center gap-2 mt-0.5 group">
                                        <span className="text-sm font-bold text-blue-600 truncate max-w-[200px]">{system.website}</span>
                                        <a
                                            href={system.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                                            title="Open in new tab"
                                        >
                                            <ExternalLink size={14} />
                                        </a>
                                    </div>
                                </div>
                            )}
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Contact Person</p>
                                <p className="text-sm font-bold text-gray-800 mt-0.5">{system.contactName || '—'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Contract Number</p>
                                <p className="text-sm font-bold text-gray-800 mt-0.5">{system.contractNumber || '—'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Contact Phone</p>
                                <div className="flex items-center justify-between group mt-0.5">
                                    <div className="flex items-center gap-2">
                                        <Phone size={14} className="text-gray-400" />
                                        <span className="text-sm font-bold text-gray-800">{system.contactPhone || '—'}</span>
                                    </div>
                                    {system.contactPhone && (
                                        <button
                                            onClick={() => copyToClipboard(system.contactPhone)}
                                            className="p-1 rounded-lg hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            <Copy size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 font-medium">Renewal Date</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <Clock size={14} className="text-gray-400" />
                                    <span className="text-sm font-bold text-gray-800">{formatDate(system.renewalDate)}</span>
                                </div>
                            </div>
                            <div className="md:col-span-2">
                                <p className="text-xs text-gray-400 font-medium">Support Email</p>
                                <div className="flex items-center justify-between group mt-0.5">
                                    <div className="flex items-center gap-2">
                                        <Mail size={14} className="text-gray-400" />
                                        <span className="text-sm font-bold text-gray-800">{system.supportEmail || '—'}</span>
                                    </div>
                                    {system.supportEmail && (
                                        <button
                                            onClick={() => copyToClipboard(system.supportEmail)}
                                            className="p-1 rounded-lg hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            <Copy size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* License Management */}
                    <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-2 mb-6">
                            <Users size={18} className="text-gray-800" />
                            <h4 className="font-bold text-gray-800">License Management</h4>
                        </div>
                        <div className="grid grid-cols-3 gap-4 mb-6">
                            <div className="text-center">
                                <p className="text-3xl font-bold text-gray-900">{system.totalLicenses || 0}</p>
                                <p className="text-xs text-gray-400 font-medium mt-1">Total Licenses</p>
                            </div>
                            <div className="text-center">
                                <p className="text-3xl font-bold text-gray-900">{system.usedLicenses || 0}</p>
                                <p className="text-xs text-gray-400 font-medium mt-1">Used</p>
                            </div>
                            <div className="text-center">
                                <p className="text-3xl font-bold text-gray-900">{availableLicenses}</p>
                                <p className="text-xs text-gray-400 font-medium mt-1">Available</p>
                            </div>
                        </div>
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <p className="text-xs text-gray-500 font-medium">Utilization</p>
                                <p className="text-xs font-bold text-gray-700">{utilization}%</p>
                            </div>
                            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all duration-1000 ${utilization > 90 ? 'bg-red-500' : utilization > 70 ? 'bg-blue-600' : 'bg-blue-500'}`}
                                    style={{ width: `${utilization}%` }}
                                />
                            </div>
                        </div>
                    </section>

                    {/* Assigned Users */}
                    <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Users size={18} className="text-gray-800" />
                                <h4 className="font-bold text-gray-800">Assigned Users ({assignedUsers.length})</h4>
                            </div>
                            {ability.can(ACTIONS.UPDATE, RESOURCES.BUSINESS_SYSTEM) && (
                                <Button
                                    size="sm"
                                    className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold gap-2"
                                    onClick={() => {
                                        fetchAllUsers();
                                        setShowAddUser(true);
                                    }}
                                >
                                    <UserPlus size={14} />
                                    Add User
                                </Button>
                            )}
                        </div>
                        <p className="text-xs text-gray-400 font-medium mb-4">Manage user access and roles for this system</p>

                        <div className="relative mb-4">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                            <Input
                                placeholder="Search users by name, email, or role..."
                                value={userSearch}
                                onChange={(e) => setUserSearch(e.target.value)}
                                className="pl-9 bg-gray-50 border-transparent rounded-xl h-10 text-sm focus:bg-white focus:border-gray-200"
                            />
                        </div>

                        {filteredUsers.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="text-[11px] uppercase tracking-wider font-bold text-gray-400 border-b border-gray-100">
                                            <th className="py-3 pr-4">User</th>
                                            <th className="py-3 pr-4">Email</th>
                                            <th className="py-3 pr-4">Department</th>
                                            <th className="py-3 pr-4">Role</th>
                                            <th className="py-3 pr-4">Assigned Date</th>
                                            <th className="py-3">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {filteredUsers.map((au: any) => (
                                            <tr key={au.id} className="hover:bg-gray-50/50 transition-colors">
                                                <td className="py-3 pr-4">
                                                    <div>
                                                        <p className="text-sm font-bold text-gray-800">{au.user?.name}</p>
                                                        <p className="text-[10px] text-gray-400">{au.user?.jobRole || au.user?.userRole}</p>
                                                    </div>
                                                </td>
                                                <td className="py-3 pr-4">
                                                    <span className="text-sm text-blue-600 font-medium">{au.user?.email}</span>
                                                </td>
                                                <td className="py-3 pr-4">
                                                    <span className="text-sm text-blue-600 font-medium">{au.user?.department?.departmentName || '—'}</span>
                                                </td>
                                                <td className="py-3 pr-4">
                                                    <Badge variant="outline" className="rounded-lg bg-gray-50 border-gray-200 text-xs font-bold">{au.role}</Badge>
                                                </td>
                                                <td className="py-3 pr-4">
                                                    <span className="text-sm text-gray-600">{formatDate(au.assignedDate)}</span>
                                                </td>
                                                <td className="py-3">
                                                    {ability.can(ACTIONS.UPDATE, RESOURCES.BUSINESS_SYSTEM) && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50"
                                                            onClick={() => setUserToRemove(au.id)}
                                                        >
                                                            <UserMinus size={16} />
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-400 text-sm">
                                No users assigned to this system
                            </div>
                        )}
                    </section>

                    {/* Activity Log */}
                    <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-2 mb-4">
                            <Clock size={18} className="text-gray-800" />
                            <h4 className="font-bold text-gray-800">Activity Log</h4>
                        </div>
                        {system.activityLogs && system.activityLogs.length > 0 ? (
                            <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
                                {system.activityLogs.map((log: any) => (
                                    <div key={log.id} className="text-sm pb-4 border-b border-gray-50 last:border-0 last:pb-0">
                                        <div className="text-gray-800">
                                            {log.changeDescription.split('|||').map((desc: string, i: number) => (
                                                <span key={i} className="block">• {desc}</span>
                                            ))}
                                        </div>
                                        <p className="text-xs text-gray-400 mt-1">
                                            {log.user ? log.user.name : 'System'} • {formatDate(log.timestamp)} at {new Date(log.timestamp).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-400 pb-2">No activity recorded</p>
                        )}
                    </section>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Quick Actions */}
                    <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <h4 className="font-bold text-gray-800 mb-4">Quick Actions</h4>
                        <div className="space-y-1">
                            <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-left">
                                <Download size={16} className="text-gray-400" />
                                <span>Export Data</span>
                                <Badge className="ml-auto rounded-full bg-amber-50 text-amber-700 border-amber-200 text-[10px] font-bold px-2 py-0 h-5">
                                    Coming soon
                                </Badge>
                            </button>
                        </div>
                    </section>


                    {/* System Information */}
                    <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <h4 className="font-bold text-gray-800 mb-4">System Information</h4>
                        <div className="space-y-3">
                            <div>
                                <p className="text-xs text-gray-400">Created</p>
                                <p className="text-sm font-bold text-gray-800">{formatDate(system.createdAt)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400">Last Updated</p>
                                <p className="text-sm font-bold text-gray-800">{formatDate(system.updatedAt)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400">Created By</p>
                                <p className="text-sm font-bold text-gray-800">
                                    {system.createdBy ? system.createdBy.name : '—'}
                                </p>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            {/* Add User Dialog */}
            <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
                <DialogContent className="rounded-2xl border-gray-100 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-bold">Add User to System</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-2">
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Users *</label>
                            <MultiSelect
                                options={allUsers.map(u => ({
                                    label: `${u.name} (${u.jobRole || 'No Role'})`,
                                    value: u.id.toString()
                                }))}
                                selected={addUserForm.userIds}
                                onChange={(val) => setAddUserForm(prev => ({ ...prev, userIds: val }))}
                                placeholder="Select Users"
                                maxDisplayTags={2}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Role</label>
                            <Select value={addUserForm.role} onValueChange={(val) => setAddUserForm(prev => ({ ...prev, role: val }))}>
                                <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-bold">
                                    <SelectValue placeholder="Select Role" />
                                </SelectTrigger>
                                <SelectContent className="rounded-2xl border-gray-100">
                                    <SelectItem value="Admin" className="font-bold py-3">Admin</SelectItem>
                                    <SelectItem value="User" className="font-bold py-3">User</SelectItem>
                                    <SelectItem value="Viewer" className="font-bold py-3">Viewer</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <Button variant="outline" onClick={() => setShowAddUser(false)} className="rounded-xl">Cancel</Button>
                            <Button onClick={handleAddUser} className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl">Add User</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Archive Confirmation */}
            <AlertDialog open={showArchiveAlert} onOpenChange={setShowArchiveAlert}>
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Archive this system?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will mark the system as archived. It can be restored later.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleArchive} className="rounded-xl bg-slate-900 hover:bg-slate-800">Archive</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Restore Confirmation */}
            <AlertDialog open={showRestoreAlert} onOpenChange={setShowRestoreAlert}>
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Restore this system?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will mark the system as active again.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleRestore} className="rounded-xl bg-green-600 hover:bg-green-700">Restore</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Confirmation */}
            <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this system?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will delete the system, but historical data will be preserved.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="rounded-xl bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Remove User Confirmation */}
            <AlertDialog open={userToRemove !== null} onOpenChange={(open) => !open && setUserToRemove(null)}>
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove this user?</AlertDialogTitle>
                        <AlertDialogDescription>
                            They will no longer be assigned to this business system.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleRemoveUser} className="rounded-xl bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            {/* Action Blocked Dialog */}
            <AlertDialog open={!!blockedAction} onOpenChange={(open) => !open && setBlockedAction(null)}>
                <AlertDialogContent className="rounded-2xl border-gray-100">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertCircle size={20} className="text-amber-600" />
                            {blockedAction?.type === 'archive' ? 'Cannot archive system' : 'Cannot delete system'}
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3 pt-2">
                                <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl text-amber-800 text-sm font-medium">
                                    You cannot {blockedAction?.type} because the system has users, remove the users and try again.
                                </div>
                                <p className="text-gray-500 text-xs px-1 font-medium text-muted-foreground">
                                    All users must be unassigned from <strong className="text-gray-700">{blockedAction?.name}</strong> before this action can be performed.
                                </p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogAction
                            onClick={() => setBlockedAction(null)}
                            className="rounded-xl bg-slate-900 hover:bg-slate-800 text-white border-0"
                        >
                            Got it
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default BusinessSystemDetails;
